import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { getLesson, getNotifications, retryNotification, deleteLesson, updateLesson } from '../../lib/api'
import { keys } from '../../lib/queryKeys'
import { StatusBadge } from '../../components/StatusBadge'
import { Entry, EntryList } from '../../components/Entry'
import { m } from '../../paraglide/messages'
import type { Lesson, ScheduledNotification } from '../../types'

const STATUSES = ['scheduled', 'completed', 'cancelled', 'no_show'] as const

// datetime-local value (YYYY-MM-DDTHH:MM) from an ISO string, in browser-local time.
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export const Route = createFileRoute('/lessons/$id')({
  component: LessonDetailPage,
})

const fmtDate = new Intl.DateTimeFormat('he-IL', { dateStyle: 'short', timeZone: 'Asia/Jerusalem' })
const fmtTime = new Intl.DateTimeFormat('he-IL', { timeStyle: 'short', timeZone: 'Asia/Jerusalem' })
const fmtDT   = new Intl.DateTimeFormat('he-IL', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Jerusalem' })

function fd(iso: string | null | undefined) {
  if (!iso) return '—'
  return fmtDate.format(new Date(iso))
}
function ft(iso: string | null | undefined) {
  if (!iso) return '—'
  return fmtTime.format(new Date(iso))
}
function fdt(iso: string | null | undefined) {
  if (!iso) return '—'
  return fmtDT.format(new Date(iso))
}

function LessonDetailPage() {
  const { id }      = Route.useParams()
  const navigate    = useNavigate()
  const queryClient = useQueryClient()

  const { data: lesson, isLoading: loadingLesson, error: lessonError } = useQuery<Lesson>({
    queryKey: keys.lesson(id),
    queryFn:  () => getLesson(id),
  })

  const remove = useMutation({
    mutationFn: () => deleteLesson(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.lessons })
      navigate({ to: '/lessons' })
    },
  })

  const { data: allNotifs } = useQuery<ScheduledNotification[]>({
    queryKey: keys.notifications,
    queryFn:  getNotifications,
  })

  const retry = useMutation({
    mutationFn: (notifId: number) => retryNotification(notifId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.notifications }),
  })

  if (loadingLesson) return <p className="text-zinc-400">{m.loading()}</p>
  if (lessonError || !lesson) return <p className="text-red-600 text-sm">{(lessonError as Error)?.message ?? 'Not found'}</p>

  const notifications = (allNotifs ?? []).filter((n) => n.lessonId === lesson.id)
  const confirmation  = lesson.confirmations?.[0]
  const showConfirmation = lesson.status === 'completed' || lesson.status === 'no_show'

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link to="/lessons" className="text-xs text-zinc-400 hover:text-zinc-900 mb-2 inline-block">
          {m.lesson_back()}
        </Link>
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-base font-semibold text-zinc-900">
            {m.lesson_word()} — {lesson.student?.name ?? '—'} — {fd(lesson.scheduledAt)}
          </h1>
          <button
            onClick={() => {
              if (confirm(m.lesson_delete_confirm())) {
                remove.mutate()
              }
            }}
            disabled={remove.isPending}
            className="border border-zinc-300 text-zinc-500 px-3 py-1.5 text-sm hover:border-red-600 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          >
            {remove.isPending ? m.lesson_deleting() : m.lesson_delete()}
          </button>
        </div>
      </div>

      {/* Lesson info (view / edit) */}
      <LessonInfo
        lesson={lesson}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: keys.lesson(id) })
          queryClient.invalidateQueries({ queryKey: keys.lessons })
          queryClient.invalidateQueries({ queryKey: keys.notifications })
        }}
      />

      {/* Confirmation block */}
      {showConfirmation && confirmation && (
        <section className="mb-8 border border-zinc-200 p-4 max-w-lg">
          <h2 className="text-sm font-semibold text-zinc-900 mb-3">{m.lesson_confirmation()}</h2>
          <table className="text-sm border-collapse w-full">
            <tbody>
              <InfoRow label={m.lesson_happened()}        value={confirmation.happened ? m.common_yes() : m.common_no()} />
              <InfoRow label={m.lesson_actual_duration()} value={confirmation.actualDurationMin ? m.duration_min({ count: confirmation.actualDurationMin }) : '—'} />
              <InfoRow label={m.lesson_confirmed_at()}    value={fdt(confirmation.confirmedAt)} />
            </tbody>
          </table>
        </section>
      )}

      {/* Notifications panel */}
      <section>
        <h2 className="text-sm font-semibold text-zinc-900 mb-3">{m.lesson_notifications()}</h2>
        <EntryList isEmpty={notifications.length === 0} emptyMessage={m.lesson_notif_empty()}>
          {notifications.map((n) => (
            <Entry
              key={n.id}
              title={n.template?.name ?? '—'}
              meta={n.renderedMsg ?? '—'}
              submeta={fdt(n.fireAt)}
              status={<StatusBadge status={n.status} />}
              actions={
                n.status === 'failed' ? (
                  <button
                    onClick={() => retry.mutate(n.id)}
                    disabled={retry.isPending}
                    className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {m.lesson_retry()}
                  </button>
                ) : undefined
              }
            />
          ))}
        </EntryList>
      </section>
    </div>
  )
}

function LessonInfo({ lesson, onSaved }: { lesson: Lesson; onSaved: () => void }) {
  const [editing, setEditing] = useState(false)
  const [when, setWhen]       = useState(toLocalInput(lesson.scheduledAt))
  const [duration, setDuration] = useState(String(lesson.durationMin))
  const [status, setStatus]   = useState<string>(lesson.status)
  const [rate, setRate]       = useState(lesson.rate)
  const [price, setPrice]     = useState(lesson.price ?? '')
  const [error, setError]     = useState('')

  const save = useMutation({
    mutationFn: () => updateLesson(lesson.id, {
      scheduledAt: when ? new Date(when).toISOString() : null,
      durationMin: Number(duration),
      status,
      rate,
      price,   // '' clears the override → back to rate × duration
    }),
    onSuccess: () => { onSaved(); setEditing(false); setError('') },
    onError: (err) => setError((err as Error).message),
  })

  const cancelEdit = () => {
    setWhen(toLocalInput(lesson.scheduledAt))
    setDuration(String(lesson.durationMin))
    setStatus(lesson.status)
    setRate(lesson.rate)
    setPrice(lesson.price ?? '')
    setError('')
    setEditing(false)
  }

  const fieldCls = 'border border-zinc-300 px-2 py-1.5 text-sm w-full focus:outline-none focus:border-zinc-900'

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-3 max-w-lg">
        <h2 className="text-sm font-semibold text-zinc-900">{m.lesson_details()}</h2>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
          >
            {m.action_edit()}
          </button>
        )}
      </div>

      {editing ? (
        <div className="flex flex-col gap-3 max-w-lg">
          <p className="text-sm text-zinc-500">{m.form_student()}: {lesson.student?.name ?? '—'}</p>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-zinc-700">{m.lesson_when()}</span>
            <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className={fieldCls} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-zinc-700">{m.form_duration()}</span>
            <input type="number" min="15" step="15" value={duration} onChange={(e) => setDuration(e.target.value)} className={fieldCls} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-zinc-700">{m.lesson_status()}</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={fieldCls}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-zinc-700">
              {m.lesson_field_rate()} <span className="text-zinc-400">{m.lesson_rate_hint()}</span>
            </span>
            <input type="number" min="0" step="any" value={rate} onChange={(e) => setRate(e.target.value)} className={fieldCls} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-zinc-700">
              {m.lesson_custom_price()} <span className="text-zinc-400">{m.lesson_custom_price_hint()}</span>
            </span>
            <input type="number" min="0" step="any" value={price} onChange={(e) => setPrice(e.target.value)} placeholder={m.lesson_price_none()} className={fieldCls} />
          </label>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <div className="flex items-center gap-3">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="bg-zinc-900 text-white px-3 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {save.isPending ? m.saving() : m.action_save()}
            </button>
            <button onClick={cancelEdit} className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100">
              {m.action_cancel()}
            </button>
          </div>
        </div>
      ) : (
        <table className="text-sm border-collapse w-full max-w-lg">
          <tbody>
            <InfoRow label={m.form_student()}      value={lesson.student?.name ?? '—'} />
            <InfoRow label={m.lesson_field_date()}  value={fd(lesson.scheduledAt)} />
            <InfoRow label={m.lesson_field_time()}  value={ft(lesson.scheduledAt)} />
            <InfoRow label={m.lesson_field_duration()} value={m.duration_min({ count: lesson.durationMin })} />
            <InfoRow label={m.lesson_field_rate()}  value={m.rate_per_hour({ amount: Number(lesson.rate).toFixed(0) })} />
            <InfoRow
              label={m.lesson_field_price()}
              value={
                lesson.effectivePrice != null
                  ? `₪${lesson.effectivePrice.toFixed(2)}${lesson.price ? ` (${m.lesson_price_custom()})` : ''}`
                  : m.lesson_price_none()
              }
            />
            <InfoRow label={m.lesson_status()}     value={<StatusBadge status={lesson.status} />} />
            <InfoRow label={m.lesson_field_paid()}  value={lesson.paid ? m.common_yes() : m.common_no()} />
          </tbody>
        </table>
      )}
    </section>
  )
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <tr className="border-b border-zinc-100">
      <td className="py-2 pr-6 text-xs text-zinc-400 uppercase tracking-wide w-32">{label}</td>
      <td className="py-2 text-sm text-zinc-900">{value}</td>
    </tr>
  )
}
